/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import type React from 'react';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';

import { type PolymorphicCommonProps } from '../_common/types';
import { CleanIconButton } from '../clean-icon-button';
import { type IconButton } from '../icon-button';
import { ChevronDownIconOutline } from '../icons/generated/heroIcons/ChevronDownIconOutline';
import { ChevronUpIconOutline } from '../icons/generated/heroIcons/ChevronUpIconOutline';
import { type LanguageProp } from './code-languages';
import { type CodeTheme } from './code-themes';
import { useCodeLanguage } from './hooks/use-code-language';
import { useCodeTheme } from './hooks/use-code-theme';
import { useExpandable } from './hooks/use-expandable';

interface CodeBlockProps {
  code: string;
  isDisabled?: boolean;
  maxHeight?: number;
  language: LanguageProp | 'text';
  shouldShowLineNumbers?: boolean;
  theme?: CodeTheme;
  heading?: React.ReactNode;
  actions?: React.ComponentProps<typeof IconButton<'button'>>[];
}

const defaultElement = 'div';

export const CodeBlock = <T extends React.ElementType = 'button'>({
  as,
  maxHeight,
  code,
  language,
  shouldShowLineNumbers,
  theme,
  heading,
  actions,
  isDisabled,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
  // TODO: Should not be polymorphic (at least not defaulting to a button)
}: PolymorphicCommonProps<T, CodeBlockProps>) => {
  const Component: React.ElementType = as ?? defaultElement;

  const {
    containerHeight,
    isExpanded,
    toggleExpand,
    hasOverflowingContent,
    ref: syntaxHighlighterWrapperRef,
  } = useExpandable(maxHeight, code);
  const codeTheme = useCodeTheme(theme);
  const codeLanguage = useCodeLanguage(language);

  const hasMaxHeight = maxHeight !== undefined;
  const hasHeading = Boolean(heading);
  const numberOfActions = actions?.length ?? 0;

  const shouldShowExpandButton = hasMaxHeight && hasOverflowingContent;
  const rightPadding = hasHeading
    ? 0
    : Math.max(numberOfActions * 36, (shouldShowExpandButton ? 1 : 0) * 36) + 8;

  return (
    <Component
      ref={ref}
      className={classNames('ndl-code-block-container', className)}
      style={{ height: containerHeight, ...style }}
      {...restProps}
      {...htmlAttributes}
    >
      <div className="n-flex n-flex-col n-h-full">
        {hasHeading && (
          <div
            className={classNames('ndl-code-block-title', {
              'ndl-disabled': isDisabled,
            })}
            style={{
              maxWidth: `calc(100% - ${(actions?.length ?? 0) * 36 + 20}px)`,
            }}
            data-testid="ndl-code-block-title"
          >
            {heading}
          </div>
        )}
        <div
          className={classNames('ndl-code-content-container', {
            'ndl-disabled': isDisabled,
          })}
          style={{
            height: hasHeading ? 'calc(100% - 24px - 12px)' : '100%',
          }}
        >
          <div
            ref={syntaxHighlighterWrapperRef}
            className="ndl-highlight-wrapper"
            tabIndex={0}
            role="region"
            aria-label={`Code content ${isDisabled ? '(disabled)' : ''}`}
          >
            <SyntaxHighlighter
              language={codeLanguage}
              style={{
                ...codeTheme,
                'pre[class*="language-"]': {
                  border: 0,
                  color: 'var(--theme-color-neutral-text-default)',
                  lineHeight: '1',
                  padding: `0 ${rightPadding}px 0.75em 0.75em`,
                  width: 'fit-content',
                },
              }}
              codeTagProps={{ className: 'n-code' }}
              // Turn on 'shouldShowLineNumbers' & 'wrapLongLines' at the same time, the display is wrong
              // https://github.com/react-syntax-highlighter/react-syntax-highlighter/issues/402
              // wrapLongLines
              showLineNumbers={shouldShowLineNumbers}
            >
              {code}
            </SyntaxHighlighter>
          </div>
        </div>
      </div>
      <div
        className={classNames('ndl-code-block-actions', {
          'ndl-disabled': isDisabled,
        })}
      >
        {actions?.map((iconButtonProps, i) => (
          <CleanIconButton
            key={i}
            isDisabled={isDisabled}
            {...iconButtonProps}
            description={`${iconButtonProps.description ?? 'CodeBlock Action'}`}
            htmlAttributes={{
              'data-testid': `ndl-action-button-${i}`,
              ...iconButtonProps.htmlAttributes,
            }}
          />
        ))}
      </div>
      {shouldShowExpandButton && (
        <div className="ndl-code-block-expand-button">
          <CleanIconButton
            description={isExpanded ? 'Collapse' : 'Expand'}
            tooltipProps={{
              root: {
                shouldCloseOnReferenceClick: true,
              },
            }}
            onClick={toggleExpand}
          >
            {isExpanded ? <ChevronUpIconOutline /> : <ChevronDownIconOutline />}
          </CleanIconButton>
        </div>
      )}
    </Component>
  );
};
